import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  X,
  Save,
  ArrowLeft,
  AlertCircle,
  Briefcase,
  Building,
  DollarSign,
  MapPin,
  Sparkles,
  CheckCircle2,
  Calendar,
  Target,
  Users,
  ChevronsUpDown
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Project } from './ProjectCard';
import { useSubscription } from '../../../hooks/useSubscription';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  email: string;
  avatar?: string;
  isOrgMember?: boolean;
  orgMemberRole?: 'admin' | 'member';
}

interface OrganizationMember {
  id: string;
  user_id: string;
  role: 'admin' | 'member';
  profile: {
    id: string;
    email: string;
    full_name: string;
    avatar_url?: string;
  };
}

interface CreateEditProjectProps {
  project?: Project;
  onSave?: (projectData: Partial<Project>) => void;
  onCancel?: () => void;
}

export function CreateEditProject({ project, onSave, onCancel }: CreateEditProjectProps) {
  const { organization } = useSubscription();
  const isEditing = !!project;
  const [organizationName, setOrganizationName] = useState<string>('');

  const [formData, setFormData] = useState({
    title: project?.title || '',
    company: organizationName || project?.company || '',
    role: project?.role || '',
    department: project?.department || '',
    location: project?.location || '',
    status: project?.status || 'draft' as Project['status'],
    priority: project?.priority || 'medium' as Project['priority'],
    deadline: project?.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    budgetMin: project?.budget?.min || 50000,
    budgetMax: project?.budget?.max || 80000,
    currency: project?.budget?.currency || '€',
    experience: project?.requirements?.experience || '',
    education: project?.requirements?.education || '',
    description: '',
    responsibilities: '',
    benefits: ''
  });

  // Separate state for skills input (as string for better UX)
  const [skillsInput, setSkillsInput] = useState(
    project?.requirements?.skills?.join(', ') || ''
  );

  const [selectedTeam, setSelectedTeam] = useState<TeamMember[]>(
    project?.team?.map(member => ({
      id: member.id,
      name: member.name,
      role: member.role,
      email: `${member.name.toLowerCase().replace(' ', '.')}@company.com`, // placeholder email
      isOrgMember: false
    })) || []
  );
  const [organizationMembers, setOrganizationMembers] = useState<OrganizationMember[]>([]);
  const [showMemberSelector, setShowMemberSelector] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Team member role selection state
  const [selectedMemberRole, setSelectedMemberRole] = useState<string>('Team Member');

  // Load organization name and members
  useEffect(() => {
    const loadOrganizationInfo = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          return;
        }

        if (profile?.organization_id) {
          const { data: organizationData } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', profile.organization_id)
            .maybeSingle();

          if (organizationData?.name) {
            setOrganizationName(organizationData.name);
            // Update formData with organization name
            setFormData(prev => ({ ...prev, company: organizationData.name }));
          }
        }
      } catch (error) {
        console.error('Error loading organization info:', error);
      }
    };

    loadOrganizationInfo();
  }, []);

  // Load organization members
  useEffect(() => {
    const loadOrganizationMembers = async () => {
      if (!organization?.organizationId) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select(`
            id,
            user_id,
            role,
            profiles:user_id (
              id,
              email,
              full_name,
              avatar_url
            )
          `)
          .eq('organization_id', organization.organizationId);

        if (error) throw error;

        const members: OrganizationMember[] = data?.map((m: any) => ({
          id: m.id,
          user_id: m.user_id,
          role: m.role,
          profile: {
            id: m.profiles?.id || '',
            email: m.profiles?.email || '',
            full_name: m.profiles?.full_name || 'Unknown User',
            avatar_url: m.profiles?.avatar_url
          }
        })) || [];

        setOrganizationMembers(members);
      } catch (error) {
        console.error('Error loading organization members:', error);
      }
    };

    loadOrganizationMembers();
  }, [organization?.organizationId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) newErrors.title = 'Project title is required';
    // Company is automatically set from organization, no validation needed
    if (!formData.role.trim()) newErrors.role = 'Role is required';
    if (!formData.department.trim()) newErrors.department = 'Department is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (formData.budgetMin >= formData.budgetMax) newErrors.budget = 'Maximum budget must be higher than minimum';
    if (!formData.experience.trim()) newErrors.experience = 'Experience requirement is required';
    
    // Convert skillsInput to array for validation
    const skillsArray = skillsInput.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0);
    if (skillsArray.length === 0) newErrors.skills = 'At least one skill is required';
    // Team members are optional - no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    const validationResult = validateForm();
    
    if (!validationResult) {
      console.log('❌ Form validation failed');
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const element = document.getElementById(firstError);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    console.log('✅ Form validation passed, creating project...');
    setIsSaving(true);
    setErrors({});

    try {
      // Convert skillsInput to array
      const skillsArray = skillsInput.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0);
      
      const projectData: Partial<Project> = {
        ...formData,
        budget: {
          min: formData.budgetMin,
          max: formData.budgetMax,
          currency: formData.currency
        },
        requirements: {
          experience: formData.experience,
          skills: skillsArray,
          education: formData.education
        },
        team: selectedTeam.map(member => ({
          id: member.id,
          name: member.name,
          role: member.role
        })),
        // Initialize progress if new project, keep existing if editing
        progress: project?.progress || { applicants: 0, interviews: 0, offers: 0, hires: 0 },
        analytics: project?.analytics || { totalViews: 0, applicationRate: 0, qualityScore: 0 },
        createdAt: project?.createdAt || new Date(),
        id: project?.id
      };

      // Call the save callback immediately
      if (onSave) {
        await onSave(projectData);
        console.log('✅ Project created successfully!');
        setSaveSuccess(true);
        
        // Navigate back after a short delay to show success message
        setTimeout(() => {
          if (onCancel) {
            onCancel();
          }
        }, 1000);
              }

    } catch (error) {
      console.error('Error saving project:', error);
      setErrors({ general: 'Failed to save project. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const removeTeamMember = (memberId: string) => {
    setSelectedTeam(prev => prev.filter(tm => tm.id !== memberId));
  };

  const addOrganizationMember = (orgMember: OrganizationMember, projectRole: string) => {
    const teamMember: TeamMember = {
      id: orgMember.user_id,
      name: orgMember.profile.full_name,
      role: projectRole,
      email: orgMember.profile.email,
      avatar: orgMember.profile.avatar_url,
      isOrgMember: true,
      orgMemberRole: orgMember.role
    };

    // Check if already added
    if (!selectedTeam.find(tm => tm.id === teamMember.id)) {
      setSelectedTeam(prev => [...prev, teamMember]);
    }
    setShowMemberSelector(false);
  };



  const getAvailableOrgMembers = () => {
    return organizationMembers.filter(orgMember => 
      !selectedTeam.find(tm => tm.id === orgMember.user_id)
    );
  };

  const handleDateChange = (dateString: string) => {
    setFormData(prev => ({
      ...prev,
      deadline: new Date(dateString)
    }));
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/90 via-blue-50/90 to-indigo-50/90 p-6">
      {/* Enhanced Header */}
      <div className="mb-8">
        <Card className="bg-gradient-to-br from-blue-50/90 via-indigo-50/90 to-purple-50/90 backdrop-blur-sm border border-blue-200/60 shadow-xl shadow-blue-200/40 rounded-3xl overflow-hidden">
          <CardHeader className="p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  className="rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 bg-white/80 hover:bg-blue-50/80 transition-all duration-200 shadow-soft"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200/50">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
                      {isEditing ? 'Edit Project' : 'Create New Project'}
                      <Sparkles className="h-5 w-5 text-yellow-500 animate-pulse" />
                    </CardTitle>
                    <CardDescription className="text-gray-600 font-medium mt-1 text-base">
                      {isEditing 
                        ? 'Update project details and team configuration' 
                        : 'Set up a new hiring project with all the details'
                      }
                    </CardDescription>
                  </div>
                </div>
              </div>
              
              {/* Progress Indicator */}
              <div className="flex items-center gap-3">
                {saveSuccess && (
                  <div className="flex items-center gap-2 bg-green-100/80 text-green-700 px-4 py-2 rounded-xl border border-green-200/60 shadow-soft animate-in slide-in-from-right-4 duration-300">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-semibold text-sm">Saved successfully!</span>
                  </div>
                )}
                {errors.general && (
                  <div className="flex items-center gap-2 bg-red-100/80 text-red-700 px-4 py-2 rounded-xl border border-red-200/60 shadow-soft">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-semibold text-sm">{errors.general}</span>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Enhanced Basic Information */}
        <Card className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/60 hover:shadow-2xl transition-all duration-500">
          <CardHeader className="p-8 pb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Building className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">Basic Information</CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Core project details and positioning
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label htmlFor="title" className="text-sm font-semibold text-gray-700">Project Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Senior Software Engineer - Backend"
                  className={`rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft ${
                    errors.title 
                      ? 'border-red-300/60 focus:border-red-500/60' 
                      : 'border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60'
                  }`}
                />
                {errors.title && <p className="text-red-600 text-sm font-medium">{errors.title}</p>}
              </div>

              <div className="space-y-3">
                <Label htmlFor="company" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  Company
                  <Building className="h-4 w-4 text-gray-500" />
                </Label>
                <Input
                  id="company"
                  value={formData.company || organizationName || 'Your Organization'}
                  readOnly
                  placeholder="Loading organization..."
                  className="rounded-xl border-2 transition-all duration-200 bg-slate-50/90 shadow-soft border-gray-200/60 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500">Company name is automatically set from your organization</p>
              </div>

              <div className="space-y-3">
                <Label htmlFor="role" className="text-sm font-semibold text-gray-700">Role *</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="Software Engineer"
                  className={`rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft ${
                    errors.role 
                      ? 'border-red-300/60 focus:border-red-500/60' 
                      : 'border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60'
                  }`}
                />
                {errors.role && <p className="text-red-600 text-sm font-medium">{errors.role}</p>}
              </div>

              <div className="space-y-3">
                <Label htmlFor="department" className="text-sm font-semibold text-gray-700">Department *</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  placeholder="Engineering"
                  className={`rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft ${
                    errors.department 
                      ? 'border-red-300/60 focus:border-red-500/60' 
                      : 'border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60'
                  }`}
                />
                {errors.department && <p className="text-red-600 text-sm font-medium">{errors.department}</p>}
              </div>

              <div className="space-y-3">
                <Label htmlFor="location" className="text-sm font-semibold text-gray-700">Location *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Berlin, Germany"
                    className={`pl-10 rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft ${
                      errors.location 
                        ? 'border-red-300/60 focus:border-red-500/60' 
                        : 'border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60'
                    }`}
                  />
                </div>
                {errors.location && <p className="text-red-600 text-sm font-medium">{errors.location}</p>}
              </div>

              <div className="space-y-3">
                <Label htmlFor="deadline" className="text-sm font-semibold text-gray-700">Deadline</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="deadline"
                    type="date"
                    value={formData.deadline.toISOString().split('T')[0]}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="pl-10 rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60 transition-all duration-200 bg-white/90 shadow-soft"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Enhanced Status & Priority */}
        <Card className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/60 hover:shadow-2xl transition-all duration-500">
          <CardHeader className="p-8 pb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">Status & Priority</CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Project status and priority level
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label htmlFor="status" className="text-sm font-semibold text-gray-700">Status</Label>
                <Select value={formData.status} onValueChange={(value: Project['status']) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 transition-all duration-200 bg-white/90 shadow-soft">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border border-gray-200/60 bg-white/95 backdrop-blur-xl shadow-xl">
                    <SelectItem value="draft" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                        Draft
                      </div>
                    </SelectItem>
                    <SelectItem value="active" className="rounded-xl hover:bg-green-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="paused" className="rounded-xl hover:bg-yellow-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        Paused
                      </div>
                    </SelectItem>
                    <SelectItem value="completed" className="rounded-xl hover:bg-blue-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        Completed
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label htmlFor="priority" className="text-sm font-semibold text-gray-700">Priority</Label>
                <Select value={formData.priority} onValueChange={(value: Project['priority']) => setFormData(prev => ({ ...prev, priority: value }))}>
                  <SelectTrigger className="rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 transition-all duration-200 bg-white/90 shadow-soft">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border border-gray-200/60 bg-white/95 backdrop-blur-xl shadow-xl">
                    <SelectItem value="low" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                        Low
                      </div>
                    </SelectItem>
                    <SelectItem value="medium" className="rounded-xl hover:bg-blue-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        Medium
                      </div>
                    </SelectItem>
                    <SelectItem value="high" className="rounded-xl hover:bg-orange-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        High
                      </div>
                    </SelectItem>
                    <SelectItem value="urgent" className="rounded-xl hover:bg-red-50/80 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                        Urgent
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requirements Section */}
        <Card className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/60 hover:shadow-2xl transition-all duration-500">
          <CardHeader className="p-8 pb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">Requirements</CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Experience level and skills requirements for this role
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8 pt-0 space-y-6">
            <div className="space-y-3">
              <Label htmlFor="experience" className="text-sm font-semibold text-gray-700">Experience Required *</Label>
              <Input
                id="experience"
                value={formData.experience}
                onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                placeholder="e.g., 3-5 years, Entry Level, Senior, etc."
                className={`rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft ${
                  errors.experience 
                    ? 'border-red-300/60 focus:border-red-500/60' 
                    : 'border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60'
                }`}
              />
              {errors.experience && <p className="text-red-600 text-sm font-medium">{errors.experience}</p>}
            </div>

            <div className="space-y-3">
              <Label htmlFor="skills" className="text-sm font-semibold text-gray-700">Required Skills *</Label>
              <div className="space-y-2">
                <Input
                  id="skills"
                  value={skillsInput}
                  onChange={(e) => setSkillsInput(e.target.value)}
                  placeholder="e.g., React, TypeScript, Node.js, AWS (comma-separated)"
                  className={`rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft ${
                    errors.skills 
                      ? 'border-red-300/60 focus:border-red-500/60' 
                      : 'border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60'
                  }`}
                />
                <p className="text-xs text-gray-500">Separate multiple skills with commas</p>
              </div>
              {errors.skills && <p className="text-red-600 text-sm font-medium">{errors.skills}</p>}
              
              {/* Skills Preview */}
              {skillsInput.trim() && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50/80 rounded-xl border border-gray-200/60">
                  {skillsInput.split(',').map((skill, index) => {
                    const trimmedSkill = skill.trim();
                    if (!trimmedSkill) return null;
                    return (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {trimmedSkill}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Label htmlFor="education" className="text-sm font-semibold text-gray-700">Education Requirements</Label>
              <Input
                id="education"
                value={formData.education}
                onChange={(e) => setFormData(prev => ({ ...prev, education: e.target.value }))}
                placeholder="e.g., Bachelor's degree in Computer Science or equivalent"
                className="rounded-xl border-2 transition-all duration-200 bg-white/90 shadow-soft border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60"
              />
            </div>
          </CardContent>
        </Card>

        {/* Enhanced Budget Section */}
        <Card className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/60 hover:shadow-2xl transition-all duration-500">
          <CardHeader className="p-8 pb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">Budget Range</CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Annual salary expectations for this role
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Label htmlFor="currency" className="text-sm font-semibold text-gray-700">Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                  <SelectTrigger className="rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 transition-all duration-200 bg-white/90 shadow-soft">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border border-gray-200/60 bg-white/95 backdrop-blur-xl shadow-xl">
                    <SelectItem value="€" className="rounded-xl hover:bg-blue-50/80 transition-colors">EUR (€)</SelectItem>
                    <SelectItem value="$" className="rounded-xl hover:bg-blue-50/80 transition-colors">USD ($)</SelectItem>
                    <SelectItem value="£" className="rounded-xl hover:bg-blue-50/80 transition-colors">GBP (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label htmlFor="budgetMin" className="text-sm font-semibold text-gray-700">Minimum Budget</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                    {formData.currency}
                  </span>
                  <Input
                    id="budgetMin"
                    type="number"
                    value={formData.budgetMin}
                    onChange={(e) => setFormData(prev => ({ ...prev, budgetMin: Number(e.target.value) }))}
                    className="pl-8 rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60 transition-all duration-200 bg-white/90 shadow-soft"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="budgetMax" className="text-sm font-semibold text-gray-700">Maximum Budget</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                    {formData.currency}
                  </span>
                  <Input
                    id="budgetMax"
                    type="number"
                    value={formData.budgetMax}
                    onChange={(e) => setFormData(prev => ({ ...prev, budgetMax: Number(e.target.value) }))}
                    className="pl-8 rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 focus:border-blue-500/60 transition-all duration-200 bg-white/90 shadow-soft"
                  />
                </div>
              </div>
            </div>
            {errors.budget && <p className="text-red-600 text-sm font-medium mt-3">{errors.budget}</p>}
            
            {/* Budget Preview */}
            <div className="mt-6 p-4 bg-gradient-to-r from-green-50/90 to-emerald-50/90 rounded-2xl border border-green-200/60">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-green-700">Budget Range:</span>
                <span className="text-lg font-bold text-green-900">
                  {formData.currency}{formData.budgetMin.toLocaleString()} - {formData.currency}{formData.budgetMax.toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Members - Optional */}
        <Card className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/60 hover:shadow-2xl transition-all duration-500">
          <CardHeader className="p-8 pb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">Team Members (Optional)</CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Add team members from your organization to collaborate on this project
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8 pt-0 space-y-6">
            {/* Organization Members Selection */}
            {organizationMembers.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-gray-700">Select Team Member</Label>
                    <Popover open={showMemberSelector} onOpenChange={setShowMemberSelector}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={showMemberSelector}
                          className="w-full justify-between rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 transition-all duration-200 bg-white/90 shadow-soft"
                          disabled={getAvailableOrgMembers().length === 0}
                        >
                          {getAvailableOrgMembers().length === 0 
                            ? "All organization members added" 
                            : "Choose from organization..."
                          }
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 rounded-2xl border border-gray-200/60 bg-white/95 backdrop-blur-xl shadow-xl">
                        <Command>
                          <CommandInput placeholder="Search team members..." className="border-none" />
                          <CommandEmpty>No organization members found.</CommandEmpty>
                          <CommandGroup>
                            {getAvailableOrgMembers().map((orgMember) => (
                              <CommandItem
                                key={orgMember.id}
                                onSelect={() => {
                                  addOrganizationMember(orgMember, selectedMemberRole);
                                }}
                                className="rounded-xl hover:bg-gray-50/80 transition-colors cursor-pointer p-4"
                              >
                                <div className="flex items-center gap-3 w-full">
                                  <Avatar className="h-10 w-10 ring-2 ring-gray-100">
                                    <AvatarImage src={orgMember.profile.avatar_url} />
                                    <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                                      {orgMember.profile.full_name.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{orgMember.profile.full_name}</p>
                                    <p className="text-xs text-gray-500 truncate">{orgMember.profile.email}</p>
                                  </div>
                                  <span className={cn(
                                    "text-xs px-3 py-1.5 rounded-full font-medium",
                                    orgMember.role === 'admin' 
                                      ? "bg-blue-100 text-blue-700" 
                                      : "bg-gray-100 text-gray-700"
                                  )}>
                                    {orgMember.role}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-gray-700">Project Role</Label>
                    <Select value={selectedMemberRole} onValueChange={setSelectedMemberRole}>
                      <SelectTrigger className="rounded-xl border-2 border-gray-200/60 hover:border-blue-300/60 transition-all duration-200 bg-white/90 shadow-soft">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl border border-gray-200/60 bg-white/95 backdrop-blur-xl shadow-xl">
                        <SelectItem value="Hiring Manager" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                          Hiring Manager
                        </SelectItem>
                        <SelectItem value="Technical Lead" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                          Technical Lead
                        </SelectItem>
                        <SelectItem value="HR Partner" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                          HR Partner
                        </SelectItem>
                        <SelectItem value="Team Member" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                          Team Member
                        </SelectItem>
                        <SelectItem value="Interviewer" className="rounded-xl hover:bg-gray-50/80 transition-colors">
                          Interviewer
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="h-16 w-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm">No organization members found</p>
              </div>
            )}

            {/* Selected Team Members Display */}
            {selectedTeam.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-700">Selected Team Members ({selectedTeam.length})</Label>
                </div>
                <div className="space-y-3">
                  {selectedTeam.map(member => (
                    <div key={member.id} className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50/90 to-gray-50/60 rounded-2xl border border-gray-200/60 hover:shadow-md transition-all duration-200">
                      <Avatar className="h-12 w-12 ring-2 ring-white shadow-lg">
                        <AvatarImage src={member.avatar} />
                        <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{member.name}</p>
                        <p className="text-xs text-gray-600 font-medium">{member.role}</p>
                        <p className="text-xs text-gray-500">{member.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs px-3 py-1.5 rounded-full font-medium bg-green-100 text-green-700">
                          Organization Member
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTeamMember(member.id)}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedTeam.length === 0 && organizationMembers.length > 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-200/60 rounded-2xl">
                <div className="h-16 w-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm font-medium mb-1">No team members added yet</p>
                <p className="text-gray-400 text-xs">Team members are optional - you can add them now or later</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <Button 
                onClick={handleSave} 
                className="w-full"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : saveSuccess ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {isEditing ? 'Updated!' : 'Created!'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEditing ? 'Update Project' : 'Create Project'}
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleCancel} 
                className="w-full"
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-2">Pro Tips</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Use clear, specific job titles</li>
                  <li>• Set realistic deadlines</li>
                  <li>• Include diverse team members</li>
                  <li>• Define skill requirements precisely</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 